/**
 * Admin Protect Section - Compliance Services with Package Selection
 * Flow: Select Package (Basic/Professional/Enterprise) → View Package-Specific Metrics
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/admin/AdminLayout';
import ClientProfileModal from '../../../components/admin/ClientProfileModal';
import { 
  Shield, Calendar, AlertTriangle, CheckCircle, Clock, Bell,
  FileText, ClipboardCheck, AlertCircle, Activity, Eye,
  RefreshCw, Loader2, Users, ArrowLeft, Zap, Star, Crown
} from 'lucide-react';

const AdminProtectSection = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedClient, setSelectedClient] = useState(null);
  
  // Client profiles database
  const clientProfiles = {
    'Solo Express LLC': {
      id: 'CLT-001',
      company: 'Solo Express LLC',
      package: 'Basic',
      contact: { name: 'James Wilson', title: 'Owner/Operator', email: 'james@soloexpress.com', phone: '(214) 555-0123' },
      address: '1234 Highway Dr, Dallas, TX 75201',
      dotNumber: '3847291',
      mcNumber: '1029384',
      fleet: { trucks: 1, trailers: 1, drivers: 1 },
      insurance: { expiry: '2026-08-15', provider: 'Progressive Commercial', coverage: '$1,000,000' },
      joined: '2025-11-15',
      stats: { totalLoads: 89, onTimeDelivery: 94, avgRating: 4.7, totalRevenue: 187500, avgRatePerMile: 2.45 },
      loadHistory: [
        { id: 'BRZ-001', route: 'Dallas, TX → Atlanta, GA', date: '2026-03-18', rate: 3200, status: 'in_transit' }
      ],
      notes: 'Reliable owner-operator. Compliance status: Good. UCR due April 15.'
    },
    'Highway Runner': {
      id: 'CLT-002',
      company: 'Highway Runner',
      package: 'Basic',
      contact: { name: 'Mike Chen', title: 'Owner', email: 'mike@highwayrunner.com', phone: '(713) 555-0456' },
      address: '567 Transport Blvd, Houston, TX 77001',
      dotNumber: '3921847',
      mcNumber: '1183726',
      fleet: { trucks: 1, trailers: 2, drivers: 1 },
      insurance: { expiry: '2026-03-28', provider: 'Northland Insurance', coverage: '$1,000,000' },
      joined: '2025-12-01',
      stats: { totalLoads: 67, onTimeDelivery: 91, avgRating: 4.5, totalRevenue: 142000, avgRatePerMile: 2.38 },
      loadHistory: [
        { id: 'BRZ-002', route: 'Houston, TX → Phoenix, AZ', date: '2026-03-21', rate: 2850, status: 'pending' }
      ],
      notes: 'Attention needed: Insurance expiring March 28. Contact for renewal.'
    },
    'Mile High Trucking': {
      id: 'CLT-003',
      company: 'Mile High Trucking',
      package: 'Basic',
      contact: { name: 'Sarah Johnson', title: 'Owner/Operator', email: 'sarah@milehigh.com', phone: '(303) 555-0789' },
      address: '890 Mountain Ave, Denver, CO 80201',
      dotNumber: '4019283',
      mcNumber: '1247583',
      fleet: { trucks: 1, trailers: 1, drivers: 1 },
      insurance: { expiry: '2026-09-10', provider: 'Great West Casualty', coverage: '$1,000,000' },
      joined: '2026-01-10',
      stats: { totalLoads: 45, onTimeDelivery: 98, avgRating: 4.9, totalRevenue: 98500, avgRatePerMile: 2.52 },
      loadHistory: [
        { id: 'BRZ-003', route: 'Los Angeles, CA → Denver, CO', date: '2026-03-15', rate: 2400, status: 'delivered' }
      ],
      notes: 'Excellent compliance. MCS-150 due May 1.'
    },
    'Eagle Transport Inc': {
      id: 'CLT-004',
      company: 'Eagle Transport Inc',
      package: 'Professional',
      contact: { name: 'Robert Martinez', title: 'Fleet Manager', email: 'robert@eagletransport.com', phone: '(206) 555-1234' },
      address: '2345 Pacific Way, Seattle, WA 98101',
      dotNumber: '2847291',
      mcNumber: '982734',
      fleet: { trucks: 3, trailers: 4, drivers: 4 },
      insurance: { expiry: '2026-07-25', provider: 'Sentry Insurance', coverage: '$2,000,000' },
      joined: '2025-08-20',
      stats: { totalLoads: 234, onTimeDelivery: 96, avgRating: 4.8, totalRevenue: 567000, avgRatePerMile: 2.78 },
      loadHistory: [
        { id: 'SLV-001', route: 'Seattle, WA → Salt Lake City, UT', date: '2026-03-17', rate: 2950, status: 'in_transit' }
      ],
      notes: 'Growing carrier. IFTA Q1 due April 30. All compliance current.'
    },
    'Swift Carriers': {
      id: 'CLT-005',
      company: 'Swift Carriers',
      package: 'Professional',
      contact: { name: 'Amanda Lee', title: 'Operations Manager', email: 'amanda@swiftcarriers.com', phone: '(503) 555-5678' },
      address: '789 Commerce St, Portland, OR 97201',
      dotNumber: '3182736',
      mcNumber: '1047382',
      fleet: { trucks: 2, trailers: 3, drivers: 3 },
      insurance: { expiry: '2026-11-30', provider: 'Progressive Commercial', coverage: '$1,500,000' },
      joined: '2025-09-15',
      stats: { totalLoads: 178, onTimeDelivery: 93, avgRating: 4.6, totalRevenue: 423000, avgRatePerMile: 2.65 },
      loadHistory: [
        { id: 'SLV-002', route: 'Portland, OR → San Francisco, CA', date: '2026-03-18', rate: 1850, status: 'in_transit' }
      ],
      notes: 'IRP due May 15. Good compliance record.'
    },
    'Prime Haul LLC': {
      id: 'CLT-011',
      company: 'Prime Haul LLC',
      package: 'Professional',
      contact: { name: 'Kevin Park', title: 'Operations Director', email: 'kevin@primehaul.com', phone: '(612) 555-4321' },
      address: '890 Commerce Blvd, Minneapolis, MN 55401',
      dotNumber: '3281947',
      mcNumber: '1083746',
      fleet: { trucks: 2, trailers: 3, drivers: 2 },
      insurance: { expiry: '2026-09-25', provider: 'Great West Casualty', coverage: '$1,500,000' },
      joined: '2025-07-10',
      stats: { totalLoads: 145, onTimeDelivery: 94, avgRating: 4.6, totalRevenue: 345000, avgRatePerMile: 2.72 },
      loadHistory: [
        { id: 'SLV-003', route: 'Minneapolis, MN → Kansas City, MO', date: '2026-03-21', rate: 1420, status: 'pending' }
      ],
      notes: 'Attention: Drug test due March 25. Pharmaceutical certified carrier.'
    },
    'Atlas Logistics': {
      id: 'CLT-006',
      company: 'Atlas Logistics',
      package: 'Enterprise',
      contact: { name: 'Michael Rodriguez', title: 'CEO', email: 'michael@atlaslogistics.com', phone: '(310) 555-9012' },
      address: '1000 Logistics Pkwy, Los Angeles, CA 90001',
      dotNumber: '1928374',
      mcNumber: '847293',
      fleet: { trucks: 8, trailers: 12, drivers: 10 },
      insurance: { expiry: '2026-12-15', provider: 'Zurich Insurance', coverage: '$5,000,000' },
      joined: '2025-03-01',
      stats: { totalLoads: 892, onTimeDelivery: 98, avgRating: 4.9, totalRevenue: 2450000, avgRatePerMile: 3.12 },
      loadHistory: [
        { id: 'GLD-001', route: 'Los Angeles, CA → Chicago, IL', date: '2026-03-17', rate: 5800, status: 'in_transit' }
      ],
      notes: 'Premium carrier. DOT Audit scheduled April 10. Full compliance management.'
    },
    'Titan Carriers': {
      id: 'CLT-007',
      company: 'Titan Carriers',
      package: 'Enterprise',
      contact: { name: 'Patricia Nguyen', title: 'VP Operations', email: 'patricia@titancarriers.com', phone: '(832) 555-3456' },
      address: '5678 Industrial Dr, Houston, TX 77002',
      dotNumber: '2019283',
      mcNumber: '912847',
      fleet: { trucks: 6, trailers: 8, drivers: 7 },
      insurance: { expiry: '2026-06-01', provider: 'Great West Casualty', coverage: '$3,000,000' },
      joined: '2025-05-10',
      stats: { totalLoads: 567, onTimeDelivery: 97, avgRating: 4.8, totalRevenue: 1780000, avgRatePerMile: 3.05 },
      loadHistory: [
        { id: 'GLD-003', route: 'Houston, TX → Los Angeles, CA', date: '2026-03-21', rate: 4650, status: 'pending' }
      ],
      notes: 'All compliance current. Insurance renewal June 1.'
    },
    'Mountain Express': {
      id: 'CLT-008',
      company: 'Mountain Express',
      package: 'Enterprise',
      contact: { name: 'Emily Jackson', title: 'Owner', email: 'emily@mountainexpress.com', phone: '(720) 555-7890' },
      address: '3456 Rocky Mountain Blvd, Denver, CO 80202',
      dotNumber: '3847261',
      mcNumber: '1129384',
      fleet: { trucks: 5, trailers: 6, drivers: 6 },
      insurance: { expiry: '2026-08-05', provider: 'Northland Insurance', coverage: '$2,500,000' },
      joined: '2025-06-20',
      stats: { totalLoads: 423, onTimeDelivery: 95, avgRating: 4.7, totalRevenue: 1250000, avgRatePerMile: 2.95 },
      loadHistory: [
        { id: 'GLD-005', route: 'Denver, CO → Kansas City, MO', date: '2026-03-19', rate: 1680, status: 'in_transit' }
      ],
      notes: 'IFTA Q1 due April 30. Excellent compliance history.'
    }
  };

  const handleClientClick = (clientName) => {
    const profile = clientProfiles[clientName];
    if (profile) {
      setSelectedClient(profile);
    }
  };
  
  // Package definitions for Protect (Compliance Services)
  const packages = {
    basic: {
      name: 'Basic',
      subtitle: 'Essential Compliance',
      price: '$197',
      priceType: '/month',
      color: '#6B7280',
      icon: Shield,
      description: 'Essential compliance monitoring for new carriers',
      features: [
        'Insurance expiration monitoring',
        'Annual UCR filing reminders',
        'Monthly compliance status report',
        'Email alerts for critical deadlines',
        'Basic regulatory guidance'
      ],
      stats: {
        activeClients: 32,
        insuranceTracked: 32,
        alertsSent: 89,
        complianceRate: 82,
        avgResponseTime: 48,
        renewalsManaged: 24
      },
      clients: [
        { name: 'Solo Express LLC', status: 'compliant', nextDeadline: 'UCR - Apr 15', alerts: 2 },
        { name: 'Highway Runner', status: 'attention', nextDeadline: 'Insurance - Mar 28', alerts: 1 },
        { name: 'Mile High Trucking', status: 'compliant', nextDeadline: 'MCS-150 - May 1', alerts: 0 }
      ]
    },
    professional: {
      name: 'Professional',
      subtitle: 'Growth Compliance',
      price: '$297',
      priceType: '/month',
      color: '#3B82F6',
      icon: Star,
      description: 'Comprehensive compliance for growing carriers',
      features: [
        'Everything in Basic',
        'IFTA & IRP deadline tracking',
        'Proactive compliance calendar',
        'Bi-weekly status updates',
        'Priority support access',
        'Safety score monitoring',
        'Audit preparation assistance'
      ],
      stats: {
        activeClients: 41,
        insuranceTracked: 78,
        alertsSent: 234,
        complianceRate: 91,
        avgResponseTime: 24,
        renewalsManaged: 67
      },
      clients: [
        { name: 'Eagle Transport Inc', status: 'compliant', nextDeadline: 'IFTA Q1 - Apr 30', alerts: 1 },
        { name: 'Swift Carriers', status: 'compliant', nextDeadline: 'IRP - May 15', alerts: 0 },
        { name: 'Prime Haul LLC', status: 'attention', nextDeadline: 'Drug Test - Mar 25', alerts: 3 }
      ]
    },
    enterprise: {
      name: 'Enterprise',
      subtitle: 'Full Compliance Management',
      price: '$397',
      priceType: '/month',
      color: '#D4AF37',
      icon: Crown,
      popular: true,
      description: 'White-glove compliance for established fleets',
      features: [
        'Everything in Professional',
        'Dedicated compliance manager',
        'Weekly compliance calls',
        'Real-time regulatory alerts',
        'Custom compliance dashboard',
        'Multi-unit fleet support',
        'Direct regulatory liaison',
        'Audit representation'
      ],
      stats: {
        activeClients: 16,
        insuranceTracked: 89,
        alertsSent: 156,
        complianceRate: 98,
        avgResponseTime: 4,
        renewalsManaged: 112
      },
      clients: [
        { name: 'Atlas Logistics', status: 'compliant', nextDeadline: 'DOT Audit - Apr 10', alerts: 0 },
        { name: 'Titan Carriers', status: 'compliant', nextDeadline: 'Insurance - Jun 1', alerts: 0 },
        { name: 'Mountain Express', status: 'compliant', nextDeadline: 'IFTA Q1 - Apr 30', alerts: 1 }
      ]
    }
  };

  // Overall stats
  const overallStats = {
    totalClients: 89,
    overallComplianceRate: 91,
    totalAlerts: 479,
    criticalIssues: 5
  };

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    setTimeout(() => setLoading(false), 500);
  }, [navigate]);

  if (loading) {
    return (
      <AdminLayout title="Protect" subtitle="Loading...">
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  // Client Profile View
  if (selectedClient) {
    return (
      <AdminLayout title="Client Profile" subtitle={selectedClient.company}>
        <button
          onClick={() => setSelectedClient(null)}
          className="flex items-center gap-2 text-white/50 hover:text-white mb-6 transition-colors"
          data-testid="protect-back-from-profile"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Compliance
        </button>
        <ClientProfileModal client={selectedClient} onClose={() => setSelectedClient(null)} isModal={false} />
      </AdminLayout>
    );
  }

  // Package Selection Screen
  if (!selectedPackage) {
    return (
      <AdminLayout 
        title="Compliance Center" 
        subtitle="Compliance services - Select a package to view metrics"
      >
        {/* Overall Summary */}
        <div className="bg-gradient-to-r from-[#10B981]/20 to-[#10B981]/5 border border-[#10B981]/30 p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-white text-lg font-bold mb-1">Compliance Services Overview</h2>
              <p className="text-white/50 text-sm">Managing {overallStats.totalClients} clients across all packages</p>
            </div>
            <div className="flex items-center gap-4 sm:gap-6">
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-[#10B981]">{overallStats.overallComplianceRate}%</p>
                <p className="text-white/40 text-xs">Compliance Rate</p>
              </div>
              <div className="h-10 w-px bg-white/10" />
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-[#3B82F6]">{overallStats.totalAlerts}</p>
                <p className="text-white/40 text-xs">Alerts Sent</p>
              </div>
              <div className="h-10 w-px bg-white/10" />
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-[#EF4444]">{overallStats.criticalIssues}</p>
                <p className="text-white/40 text-xs">Critical Issues</p>
              </div>
            </div>
          </div>
        </div>

        {/* Package Selection Cards */}
        <h3 className="text-white font-bold mb-4">Select a Package to View Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(packages).map(([key, pkg]) => (
            <div
              key={key}
              onClick={() => setSelectedPackage(key)}
              className={`bg-[#121212] border p-6 cursor-pointer hover:bg-[#1a1a1a] transition-all relative ${
                pkg.popular ? 'border-[#D4AF37]' : 'border-white/10 hover:border-white/20'
              }`}
              data-testid={`protect-package-${key}`}
            >
              {pkg.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-[#D4AF37] text-black text-xs font-bold px-3 py-1">MOST POPULAR</span>
                </div>
              )}
              
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 rounded" style={{ backgroundColor: `${pkg.color}20` }}>
                  <pkg.icon className="w-6 h-6" style={{ color: pkg.color }} />
                </div>
                <div>
                  <p className="text-white/50 text-xs uppercase tracking-wider">{pkg.name}</p>
                  <p className="text-white font-bold">{pkg.subtitle}</p>
                </div>
              </div>

              <div className="mb-4">
                <span className="text-3xl font-bold" style={{ color: pkg.color }}>{pkg.price}</span>
                <span className="text-white/40 text-sm">{pkg.priceType}</span>
              </div>

              <p className="text-white/50 text-sm mb-4">{pkg.description}</p>

              <div className="border-t border-white/10 pt-4 mt-4">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-white/40 text-xs">Active Clients</p>
                    <p className="text-white font-bold text-xl">{pkg.stats.activeClients}</p>
                  </div>
                  <div>
                    <p className="text-white/40 text-xs">Compliance Rate</p>
                    <p className="text-[#10B981] font-bold text-xl">{pkg.stats.complianceRate}%</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-white/50 text-sm">Alerts Sent</span>
                  <span className="text-white font-bold">{pkg.stats.alertsSent}</span>
                </div>
              </div>

              <button 
                className="w-full mt-4 py-3 text-sm font-medium transition-all"
                style={{ 
                  backgroundColor: `${pkg.color}20`, 
                  color: pkg.color,
                  border: `1px solid ${pkg.color}40`
                }}
              >
                View {pkg.name} Metrics →
              </button>
            </div>
          ))}
        </div>
      </AdminLayout>
    );
  }

  // Package-Specific Metrics Screen
  const pkg = packages[selectedPackage];

  return (
    <AdminLayout 
      title={`${pkg.name} Package`} 
      subtitle={pkg.subtitle}
    >
      {/* Back Button */}
      <button
        onClick={() => setSelectedPackage(null)}
        className="flex items-center gap-2 text-white/50 hover:text-white mb-6 transition-colors"
        data-testid="protect-back-button"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to All Packages
      </button>

      {/* Package Header */}
      <div className="border p-4 sm:p-6 mb-6" style={{ 
        backgroundColor: `${pkg.color}10`, 
        borderColor: `${pkg.color}40` 
      }}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded" style={{ backgroundColor: `${pkg.color}20` }}>
              <pkg.icon className="w-8 h-8" style={{ color: pkg.color }} />
            </div>
            <div>
              <h2 className="text-white text-xl font-bold">{pkg.name} - {pkg.subtitle}</h2>
              <p className="text-white/50">{pkg.price}{pkg.priceType} • {pkg.stats.activeClients} active clients</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-[#10B981]">{pkg.stats.complianceRate}%</p>
              <p className="text-white/40 text-xs">Compliance Rate</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-6">
        <StatCard icon={Users} label="Active Clients" value={pkg.stats.activeClients} color={pkg.color} onClick={() => setActiveTab('clients')} />
        <StatCard icon={Shield} label="Insurance Tracked" value={pkg.stats.insuranceTracked} color="#3B82F6" onClick={() => setActiveTab('insurance')} />
        <StatCard icon={Bell} label="Alerts Sent" value={pkg.stats.alertsSent} color="#F59E0B" onClick={() => setActiveTab('alerts')} />
        <StatCard icon={CheckCircle} label="Compliance Rate" value={`${pkg.stats.complianceRate}%`} color="#10B981" onClick={() => setActiveTab('overview')} />
        <StatCard icon={Clock} label="Avg Response (hrs)" value={pkg.stats.avgResponseTime} color="#8B5CF6" onClick={() => setActiveTab('overview')} />
        <StatCard icon={Calendar} label="Renewals Managed" value={pkg.stats.renewalsManaged} color="#EC4899" onClick={() => setActiveTab('deadlines')} />
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 mb-6 border-b border-white/10 pb-4 overflow-x-auto">
        {[
          { id: 'overview', label: 'Overview', icon: Eye },
          { id: 'clients', label: 'Clients', icon: Users },
          { id: 'insurance', label: 'Insurance', icon: Shield },
          { id: 'deadlines', label: 'Deadlines', icon: Calendar },
          { id: 'alerts', label: 'Alerts', icon: Bell },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`protect-tab-${tab.id}`}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === tab.id
                ? 'bg-[#10B981]/10 border border-[#10B981]/30'
                : 'text-white/50 hover:text-white border border-transparent'
            }`}
            style={{ color: activeTab === tab.id ? pkg.color : undefined }}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Package Features */}
          <div className="bg-[#121212] border border-white/10 p-6">
            <h3 className="text-white font-bold mb-4">Package Features</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {pkg.features.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: `${pkg.color}20` }}>
                    <Zap className="w-3 h-3" style={{ color: pkg.color }} />
                  </div>
                  <span className="text-white/70 text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-[#121212] border border-white/10 p-4 text-center">
              <p className="text-3xl font-bold text-white">{pkg.stats.activeClients}</p>
              <p className="text-white/50 text-xs">Active Clients</p>
            </div>
            <div className="bg-[#121212] border border-white/10 p-4 text-center">
              <p className="text-3xl font-bold text-[#10B981]">{pkg.stats.complianceRate}%</p>
              <p className="text-white/50 text-xs">Compliance Rate</p>
            </div>
            <div className="bg-[#121212] border border-white/10 p-4 text-center">
              <p className="text-3xl font-bold text-[#F59E0B]">{pkg.stats.alertsSent}</p>
              <p className="text-white/50 text-xs">Alerts Sent</p>
            </div>
            <div className="bg-[#121212] border border-white/10 p-4 text-center">
              <p className="text-3xl font-bold text-[#8B5CF6]">{pkg.stats.avgResponseTime}h</p>
              <p className="text-white/50 text-xs">Avg Response</p>
            </div>
          </div>
        </div>
      )}

      {/* Clients Tab */}
      {activeTab === 'clients' && (
        <div className="bg-[#121212] border border-white/10 overflow-hidden">
          <div className="p-4 border-b border-white/10 flex justify-between items-center">
            <h3 className="text-white font-bold">{pkg.name} Package Clients</h3>
            <span className="text-white/50 text-sm">{pkg.stats.activeClients} total</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left text-white/50 text-xs uppercase p-3">Client</th>
                  <th className="text-left text-white/50 text-xs uppercase p-3">Status</th>
                  <th className="text-left text-white/50 text-xs uppercase p-3">Next Deadline</th>
                  <th className="text-left text-white/50 text-xs uppercase p-3">Active Alerts</th>
                </tr>
              </thead>
              <tbody>
                {pkg.clients.map((client, idx) => (
                  <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-3">
                      <button 
                        onClick={() => handleClientClick(client.name)}
                        className="text-white font-medium hover:text-[#10B981] hover:underline transition-colors text-left"
                        data-testid={`protect-client-${idx}`}
                      >
                        {client.name}
                      </button>
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-1 text-xs ${
                        client.status === 'compliant' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {client.status}
                      </span>
                    </td>
                    <td className="p-3 text-white/70">{client.nextDeadline}</td>
                    <td className="p-3">
                      <span className={`font-bold ${client.alerts > 0 ? 'text-[#F59E0B]' : 'text-white/50'}`}>
                        {client.alerts}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Insurance Tab */}
      {activeTab === 'insurance' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-[#121212] border border-white/10 p-6 text-center">
              <Shield className="w-12 h-12 text-[#3B82F6] mx-auto mb-3" />
              <p className="text-4xl font-bold text-white">{pkg.stats.insuranceTracked}</p>
              <p className="text-white/50">Policies Tracked</p>
            </div>
            <div className="bg-[#121212] border border-white/10 p-6 text-center">
              <Calendar className="w-12 h-12 text-[#EC4899] mx-auto mb-3" />
              <p className="text-4xl font-bold text-white">{pkg.stats.renewalsManaged}</p>
              <p className="text-white/50">Renewals Managed</p>
            </div>
            <div className="bg-[#121212] border border-white/10 p-6 text-center">
              <Bell className="w-12 h-12 text-[#F59E0B] mx-auto mb-3" />
              <p className="text-4xl font-bold text-white">{pkg.stats.alertsSent}</p>
              <p className="text-white/50">Alerts Sent</p>
            </div>
          </div>
        </div>
      )}

      {/* Deadlines Tab */}
      {activeTab === 'deadlines' && (
        <div className="bg-[#121212] border border-white/10 p-6">
          <h3 className="text-white font-bold mb-4">Upcoming Deadlines</h3>
          <div className="space-y-3">
            {pkg.clients.map((client, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-[#0A0A0A] border border-white/10">
                <div>
                  <button 
                    onClick={() => handleClientClick(client.name)}
                    className="text-white font-medium hover:text-[#10B981] hover:underline transition-colors text-left"
                    data-testid={`protect-deadline-client-${idx}`}
                  >
                    {client.name}
                  </button>
                  <p className="text-white/50 text-sm">{client.nextDeadline}</p>
                </div>
                <span className={`px-2 py-1 text-xs ${
                  client.status === 'compliant' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                }`}>
                  {client.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Alerts Tab */}
      {activeTab === 'alerts' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-[#121212] border border-[#F59E0B]/30 p-6">
              <Bell className="w-8 h-8 text-[#F59E0B] mb-3" />
              <p className="text-4xl font-bold text-[#F59E0B]">{pkg.stats.alertsSent}</p>
              <p className="text-white/50">Total Alerts Sent</p>
            </div>
            <div className="bg-[#121212] border border-[#10B981]/30 p-6">
              <CheckCircle className="w-8 h-8 text-[#10B981] mb-3" />
              <p className="text-4xl font-bold text-[#10B981]">{pkg.stats.complianceRate}%</p>
              <p className="text-white/50">Compliance Rate</p>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, color, onClick }) => (
  <div 
    onClick={onClick}
    className="bg-[#121212] border border-white/10 p-4 sm:p-5 cursor-pointer hover:bg-[#1a1a1a] hover:border-white/20 transition-all"
  >
    <div className="flex items-center gap-3 mb-3">
      <div className="p-2 rounded flex-shrink-0" style={{ backgroundColor: `${color}20` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <span className="text-white/50 text-sm">{label}</span>
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
  </div>
);

export default AdminProtectSection;
